import React, { useState, useRef, useEffect, useCallback } from 'react';
import OpenAI from 'openai';
import { storage, auth } from '../firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { vibePresets } from '../data/vibePresets';
import { soundEffects } from '../data/constants'; // --- NEW ---: Imported from constants
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { toBlobURL } from '@ffmpeg/util';
import { FilmIcon, PlayIcon } from '@heroicons/react/24/solid';

import './ImageAnimatorPage.css';

const openAiVoices = [
    { name: "alloy", label: "Alloy -F" }, { name: "ash", label: "Ash -M" }, { name: "ballad", label: "Ballad -M" },
    { name: "coral", label: "Coral -F" }, { name: "echo", label: "Echo -M" }, { name: "fable", label: "Fable -M" },
    { name: "nova", label: "Nova -F" }, { name: "onyx", label: "Onyx -M " }, { name: "sage", label: "Sage -F" }, { name: "verse", label: "Verse -M" },
    { name: "shimmer", label: "Shimmer -F" }
];

const visualFilters = [
    { name: 'none', label: 'None' }, { name: 'grayscale(100%)', label: 'Grayscale' },
    { name: 'sepia(100%)', label: 'Sepia' }, { name: 'saturate(2)', label: 'Saturate' },
    { name: 'contrast(150%)', label: 'Contrast' }, { name: 'brightness(1.2)', label: 'Brighten' },
    { name: 'invert(100%)', label: 'Invert' }
];

const transitionTypes = [
    { value: 'cross-fade', label: 'Cross-Fade' },
    { value: 'fade-to-black', label: 'Fade to Black' },
    { value: 'slide-left', label: 'Slide Left' },
    { value: 'slide-right', label: 'Slide Right' },
    { value: 'slide-up', label: 'Slide Up' },
    { value: 'slide-down', label: 'Slide Down' },
];

const getAudioDuration = (blob) => {
    return new Promise((resolve, reject) => {
        if (!blob || blob.size === 0) return resolve(0);
        const audio = document.createElement('audio');
        const objectUrl = URL.createObjectURL(blob);
        audio.src = objectUrl;
        audio.onloadedmetadata = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(audio.duration);
        };
        audio.onerror = (e) => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error("Failed to load audio metadata for duration calculation."));
        };
    });
};

const createInstructionsFromVibe = (vibe) => {
    if (!vibe) return undefined;
    const instructions = Object.entries(vibe)
        .filter(([key, value]) => key !== 'open' && key !== 'preset' && value)
        .map(([key, value]) => {
            const formattedKey = key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
            return `${formattedKey}: ${value}`;
        })
        .join('\n\n');
    return instructions || undefined;
};


const ImageAnimatorPage = () => {
    const [imageSequence, setImageSequence] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingStatus, setProcessingStatus] = useState('');
    const [draggedId, setDraggedId] = useState(null);
    const [openaiKey, setOpenaiKey] = useState(process.env.REACT_APP_OPENAI_API_KEY || "");
    const [openaiInstance, setOpenaiInstance] = useState(null);

    const [globalVoice, setGlobalVoice] = useState('alloy');
    const [globalVibe, setGlobalVibe] = useState({ ...vibePresets["No Vibe"] });

    const [transitionType, setTransitionType] = useState('cross-fade');
    const [transitionDuration, setTransitionDuration] = useState(500);
    const [isPreviewing, setIsPreviewing] = useState(false);

    const [transitionSound, setTransitionSound] = useState(null);
    const [transitionSoundVolume, setTransitionSoundVolume] = useState(1); // --- NEW ---: Volume state

    const [outputFilename, setOutputFilename] = useState('JustAutomateIt-animation');
    const [ffmpeg, setFfmpeg] = useState(null);
    const [isFfmpegLoaded, setIsFfmpegLoaded] = useState(false);
    const [isConverting, setIsConverting] = useState(false);
    const [conversionProgress, setConversionProgress] = useState(0);
    const [webmToConvert, setWebmToConvert] = useState(null);

    const [showProgressBar, setShowProgressBar] = useState(true);
    const [progressBarColor, setProgressBarColor] = useState('#8B5CF6');

    const [useDurationBuffer, setUseDurationBuffer] = useState(true);

    const canvasRef = useRef(null);
    const animationFrameId = useRef(null);
    const previewAbortController = useRef(null);
    const ttsCache = useRef(new Map());
    const previewAudioRef = useRef(null);


    const selectedItem = selectedIndex !== -1 ? imageSequence[selectedIndex] : null;

    useEffect(() => {
        const loadFfmpeg = async () => {
            const ffmpegInstance = new FFmpeg();
            ffmpegInstance.on('log', ({ message }) => console.log(message));
            ffmpegInstance.on('progress', ({ progress }) => {
                if (progress >= 0 && progress <= 1) setConversionProgress(Math.round(progress * 100));
            });
            const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd'
            await ffmpegInstance.load({
                coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
                wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
            });
            setFfmpeg(ffmpegInstance);
            setIsFfmpegLoaded(true);
        };
        loadFfmpeg();
    }, []);

    useEffect(() => {
        if (openaiKey) {
            try {
                setOpenaiInstance(new OpenAI({ apiKey: openaiKey, dangerouslyAllowBrowser: true }));
            } catch (error) {
                console.error("Error initializing OpenAI instance:", error);
                setOpenaiInstance(null);
            }
        } else {
            setOpenaiInstance(null);
        }
    }, [openaiKey]);

    useEffect(() => {
        if (imageSequence.length > 0) {
            setImageSequence(prev => prev.map(item => ({ ...item, voice: globalVoice })));
        }
    }, [globalVoice, imageSequence.length]);

    useEffect(() => {
        if (imageSequence.length > 0) {
            setImageSequence(prev => prev.map(item => ({ ...item, vibe: globalVibe })));
        }
    }, [globalVibe, imageSequence.length]);

    const handleScriptUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const loadedData = JSON.parse(event.target.result);
                const scriptTexts = Object.values(loadedData);
                if (imageSequence.length === 0) {
                    alert("Please upload images before uploading a script file.");
                    return;
                }
                setImageSequence(prevSequence =>
                    prevSequence.map((item, index) => {
                        if (scriptTexts[index]) return { ...item, script: scriptTexts[index] };
                        return item;
                    })
                );
                alert(`${scriptTexts.length} scripts were loaded and applied to the timeline!`);
            } catch (error) {
                console.error("Error parsing script JSON:", error);
                alert("Failed to load script. Please ensure it's a valid JSON file.");
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    };

    const handleGenerateAudio = async () => {
        if (!selectedItem || !selectedItem.script.trim()) return;
        if (!openaiInstance) { alert("Please enter a valid OpenAI API key."); return; }
        setImageSequence(prev => prev.map((item, index) => index === selectedIndex ? { ...item, isGeneratingAudio: true } : item));
        const { script: text, voice, vibe } = selectedItem;
        const instructions = createInstructionsFromVibe(vibe);
        const cacheKey = `tts-timestamps-v2:${voice}:${instructions || ''}:${text}`;
        try {
            let audioUrl, audioDurationVal, words;
            if (ttsCache.current.has(cacheKey)) {
                ({ url: audioUrl, duration: audioDurationVal, words } = ttsCache.current.get(cacheKey));
            } else {
                const speechResponse = await openaiInstance.audio.speech.create({ model: "gpt-4o-mini-tts", input: text, voice });
                const blob = await speechResponse.blob();
                audioDurationVal = await getAudioDuration(blob);
                const transcriptionResponse = await openaiInstance.audio.transcriptions.create({
                    model: "whisper-1",
                    file: new File([blob], "speech.mp3", { type: "audio/mpeg" }),
                    response_format: "verbose_json",
                    timestamp_granularities: ["word"]
                });
                words = transcriptionResponse.words;
                const userId = auth.currentUser ? auth.currentUser.uid : 'anonymous';
                const fileName = `tts-audio/${userId}/${Date.now()}-${voice}.mp3`;
                const storageRef = ref(storage, fileName);
                await uploadBytes(storageRef, blob, { contentType: 'audio/mpeg' });
                audioUrl = await getDownloadURL(storageRef);
                ttsCache.current.set(cacheKey, { url: audioUrl, duration: audioDurationVal, words });
            }

            const newDuration = useDurationBuffer
                ? Math.round((audioDurationVal * 1000) + 1000)
                : Math.round(audioDurationVal * 1000);

            setImageSequence(prev => prev.map((item, index) => {
                if (index === selectedIndex) return { ...item, audioUrl, duration: newDuration, words, isGeneratingAudio: false };
                return item;
            }));
        } catch (error) {
            console.error("Error generating or transcribing audio:", error);
            alert(`Failed to generate audio and timestamps: ${error.message}`);
            setImageSequence(prev => prev.map((item, index) => index === selectedIndex ? { ...item, isGeneratingAudio: false } : item));
        }
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        const newImages = files.map(file => {
            return new Promise(resolve => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const img = new Image();
                    img.src = event.target.result;
                    img.onload = () => resolve({
                        id: Date.now() + Math.random(),
                        image: img,
                        effect: 'kenburns-up-right', duration: 3000, zoomAmount: 0.1, panAmount: 0.05,
                        script: '', audioUrl: null, words: [], isGeneratingAudio: false,
                        voice: globalVoice, vibe: globalVibe,
                        textOverlay: {
                            enabled: false, // --- MODIFIED ---: Captions disabled by default
                            font: 'Arial',
                            size: 16,
                            color: '#FFFFFF', strokeColor: '#000000', strokeWidth: 2,
                            yPosition: 85,
                        },
                        filter: 'none',
                    });
                };
                reader.readAsDataURL(file);
            });
        });
        Promise.all(newImages).then(imgs => {
            setImageSequence(prev => [...prev, ...imgs]);
            if (selectedIndex === -1 && imgs.length > 0) setSelectedIndex(0);
        });
    };

    const handleControlChange = (field, value) => {
        if (!selectedItem) return;
        setImageSequence(prev => prev.map((item, index) => index === selectedIndex ? { ...item, [field]: value } : item));
    };

    const handleTextOverlayChange = (prop, value) => {
        if (!selectedItem) return;
        setImageSequence(prev => prev.map((item, index) => {
            if (index === selectedIndex) {
                const newTextOverlay = { ...item.textOverlay, [prop]: value };
                return { ...item, textOverlay: newTextOverlay };
            }
            return item;
        }));
    };

    const handleVibePresetChange = (presetName) => {
        setGlobalVibe(prev => ({ ...vibePresets[presetName], open: prev.open, preset: presetName }));
    };

    const handleVibeDetailChange = (key, value) => {
        setGlobalVibe(prev => ({ ...prev, [key]: value, preset: "Custom" }));
    };

    const getTransform = useCallback((item, progress) => {
        let scale = 1, xOffset = 0, yOffset = 0;
        const p = Math.min(Math.max(progress, 0), 1);
        const pan = item.panAmount * 100;

        switch (item.effect) {
            case 'kenburns-up-right': scale = 1 + item.zoomAmount * p; xOffset = pan * p; yOffset = -pan * p; break;
            case 'kenburns-down-right': scale = 1 + item.zoomAmount * p; xOffset = pan * p; yOffset = pan * p; break;
            case 'kenburns-up-left': scale = 1 + item.zoomAmount * p; xOffset = -pan * p; yOffset = -pan * p; break;
            case 'kenburns-down-left': scale = 1 + item.zoomAmount * p; xOffset = -pan * p; yOffset = pan * p; break;
            case 'zoom-in': scale = 1 + item.zoomAmount * p; break;
            case 'zoom-out': scale = 1 + item.zoomAmount * (1 - p); break;
            default: break;
        }
        return { scale, xOffset, yOffset };
    }, []);

    const drawImage = useCallback((img, { scale = 1, xOffset = 0, yOffset = 0, alpha = 1, filter = 'none' }) => {
        const canvas = canvasRef.current;
        if (!canvas || !img) return;
        const ctx = canvas.getContext('2d');

        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.filter = filter;

        const canvasAspect = canvas.width / canvas.height;
        const imgAspect = img.width / img.height;
        let renderWidth, renderHeight, x, y;

        if (imgAspect > canvasAspect) {
            renderHeight = canvas.height * scale;
            renderWidth = img.width * (renderHeight / img.height);
        } else {
            renderWidth = canvas.width * scale;
            renderHeight = img.height * (renderWidth / img.width);
        }

        x = (canvas.width - renderWidth) / 2 + xOffset;
        y = (canvas.height - renderHeight) / 2 + yOffset;

        ctx.drawImage(img, x, y, renderWidth, renderHeight);

        ctx.restore();
    }, []);

    const drawTextOverlay = useCallback((item, timeMsInScene) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const { textOverlay, words } = item;
        if (!textOverlay.enabled || !words || words.length === 0) return;

        const timeSec = timeMsInScene / 1000;

        ctx.font = `bold ${textOverlay.size}px ${textOverlay.font}`;
        ctx.textBaseline = 'bottom';
        ctx.lineWidth = textOverlay.strokeWidth;

        const padding = canvas.width * 0.05;
        const maxWidth = canvas.width - (padding * 2);

        const wrapText = (text) => {
            let lines = [];
            let currentLine = '';
            let wordsInText = text.split(' ');
            for (let word of wordsInText) {
                let testLine = currentLine + word + ' ';
                if (ctx.measureText(testLine).width > maxWidth && currentLine !== '') {
                    lines.push(currentLine.trim());
                    currentLine = word + ' ';
                } else {
                    currentLine = testLine;
                }
            }
            lines.push(currentLine.trim());
            return lines;
        }

        const fullText = words.map(w => w.word).join(" ");
        const lines = wrapText(fullText);
        const lineHeight = textOverlay.size * 1.2;
        let startY = (canvas.height * textOverlay.yPosition / 100) - ((lines.length - 1) * lineHeight);

        let highlightInfo = { totalHighlightedChars: 0 };
        for (const word of words) {
            const wordWithSpace = word.word + " ";
            if (timeSec >= word.end) {
                highlightInfo.totalHighlightedChars += wordWithSpace.length;
            } else if (timeSec >= word.start) {
                const wordDuration = word.end - word.start;
                const timeIntoWord = timeSec - word.start;
                const wordProgress = wordDuration > 0 ? timeIntoWord / wordDuration : 1;
                highlightInfo.totalHighlightedChars += Math.floor(wordWithSpace.length * wordProgress);
                break;
            }
        }

        lines.forEach((line, lineIndex) => {
            const y = startY + (lineIndex * lineHeight);
            const x = (canvas.width - ctx.measureText(line).width) / 2;

            ctx.strokeStyle = textOverlay.strokeColor;
            ctx.fillStyle = '#AAAAAA';
            ctx.strokeText(line, x, y);
            ctx.fillText(line, x, y);

            const charsInPreviousLines = lines.slice(0, lineIndex).join(" ").length + (lineIndex > 0 ? 1 : 0);
            const charsToHighlightInLine = Math.max(0, highlightInfo.totalHighlightedChars - charsInPreviousLines);

            if (charsToHighlightInLine > 0) {
                const highlightedText = line.substring(0, charsToHighlightInLine);
                const highlightedWidth = ctx.measureText(highlightedText).width;

                ctx.save();
                ctx.beginPath();
                ctx.rect(x, y - lineHeight, highlightedWidth, lineHeight * 1.5);
                ctx.clip();

                ctx.fillStyle = textOverlay.color;
                ctx.strokeText(line, x, y);
                ctx.fillText(line, x, y);

                ctx.restore();
            }
        });

    }, []);

    const drawProgressBar = useCallback((progress) => {
        if (!showProgressBar) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const barHeight = 16;
        const y = canvas.height - barHeight;

        ctx.fillStyle = 'rgba(100, 116, 139, 0.5)';
        ctx.fillRect(0, y, canvas.width, barHeight);

        ctx.fillStyle = progressBarColor;
        ctx.fillRect(0, y, canvas.width * progress, barHeight);
    }, [showProgressBar, progressBarColor]);


    const drawFrameForTime = useCallback((timeMs, totalDurationMs) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        let cumulativeTime = 0;
        let drawn = false;

        for (let i = 0; i < imageSequence.length; i++) {
            const item = imageSequence[i];
            const nextItem = imageSequence[i + 1];
            const itemDuration = item.duration;
            const transDuration = nextItem ? transitionDuration : 0;

            // Current Scene
            if (timeMs >= cumulativeTime && timeMs < cumulativeTime + itemDuration) {
                const progress = (timeMs - cumulativeTime) / itemDuration;
                drawImage(item.image, { ...getTransform(item, progress), filter: item.filter });
                drawTextOverlay(item, timeMs - cumulativeTime);
                drawn = true;
                break;
            }
            cumulativeTime += itemDuration;

            // Transition
            if (nextItem && timeMs >= cumulativeTime && timeMs < cumulativeTime + transDuration) {
                const progress = (timeMs - cumulativeTime) / transDuration;
                const outTransform = getTransform(item, 1.0);
                const inTransform = getTransform(nextItem, 0);

                ctx.save();
                ctx.clearRect(0, 0, canvas.width, canvas.height); // Clear for transitions that need it
                switch (transitionType) {
                    case 'cross-fade':
                        drawImage(item.image, { ...outTransform, alpha: 1 - progress, filter: item.filter });
                        ctx.globalAlpha = progress;
                        drawImage(nextItem.image, { ...inTransform, filter: nextItem.filter });
                        break;
                    case 'fade-to-black':
                        if (progress < 0.5) {
                            drawImage(item.image, { ...outTransform, alpha: 1 - (progress * 2), filter: item.filter });
                        } else {
                            drawImage(nextItem.image, { ...inTransform, alpha: (progress - 0.5) * 2, filter: nextItem.filter });
                        }
                        break;
                    case 'slide-left':
                        drawImage(item.image, { ...outTransform, xOffset: outTransform.xOffset - progress * canvas.width, filter: item.filter });
                        drawImage(nextItem.image, { ...inTransform, xOffset: inTransform.xOffset + canvas.width * (1 - progress), filter: nextItem.filter });
                        break;
                    case 'slide-right':
                        drawImage(item.image, { ...outTransform, xOffset: outTransform.xOffset + progress * canvas.width, filter: item.filter });
                        drawImage(nextItem.image, { ...inTransform, xOffset: inTransform.xOffset - canvas.width * (1 - progress), filter: nextItem.filter });
                        break;
                    case 'slide-up':
                        drawImage(item.image, { ...outTransform, yOffset: outTransform.yOffset - progress * canvas.height, filter: item.filter });
                        drawImage(nextItem.image, { ...inTransform, yOffset: inTransform.yOffset + canvas.height * (1 - progress), filter: nextItem.filter });
                        break;
                    case 'slide-down':
                        drawImage(item.image, { ...outTransform, yOffset: outTransform.yOffset + progress * canvas.height, filter: item.filter });
                        drawImage(nextItem.image, { ...inTransform, yOffset: inTransform.yOffset - canvas.height * (1 - progress), filter: nextItem.filter });
                        break;
                    default:
                        drawImage(item.image, { ...outTransform, filter: item.filter });
                        break;
                }
                ctx.restore();
                drawn = true;
                break;
            }
            cumulativeTime += transDuration;
        }

        if (!drawn && imageSequence.length > 0) {
            const lastItem = imageSequence[imageSequence.length - 1];
            drawImage(lastItem.image, { ...getTransform(lastItem, 1.0), filter: lastItem.filter });
            drawTextOverlay(lastItem, lastItem.duration);
        }

        if (totalDurationMs > 0) {
            const overallProgress = Math.min(timeMs / totalDurationMs, 1);
            drawProgressBar(overallProgress);
        }

    }, [imageSequence, transitionDuration, transitionType, getTransform, drawImage, drawTextOverlay, drawProgressBar]);

    const handlePreviewAnimation = async () => {
        if (isPreviewing) return;
        setIsPreviewing(true);
        previewAbortController.current = new AbortController();
        const signal = previewAbortController.current.signal;

        const totalDuration = imageSequence.reduce((acc, item, i) => acc + item.duration + (i < imageSequence.length - 1 ? transitionDuration : 0), 0);

        const transitionTimes = [];
        let cumulativeTime = 0;
        imageSequence.forEach((item, i) => {
            cumulativeTime += item.duration;
            if (i < imageSequence.length - 1) {
                transitionTimes.push(cumulativeTime);
                cumulativeTime += transitionDuration;
            }
        });
        let lastPlayedTransition = -1;

        let startTime = performance.now();
        const renderLoop = (currentTime) => {
            if (signal.aborted) return;
            const elapsedTime = currentTime - startTime;

            if (transitionSound) {
                for (let i = 0; i < transitionTimes.length; i++) {
                    if (elapsedTime >= transitionTimes[i] && lastPlayedTransition < i) {
                        if (previewAudioRef.current) {
                            previewAudioRef.current.currentTime = 0;
                            previewAudioRef.current.play();
                        }
                        lastPlayedTransition = i;
                        break;
                    }
                }
            }

            if (elapsedTime > totalDuration) {
                drawFrameForTime(totalDuration, totalDuration);
                setIsPreviewing(false);
                return;
            }
            drawFrameForTime(elapsedTime, totalDuration);
            animationFrameId.current = requestAnimationFrame(renderLoop);
        };
        animationFrameId.current = requestAnimationFrame(renderLoop);
    };

    const handleStopPreview = () => {
        if (previewAbortController.current) previewAbortController.current.abort();
        if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
        if (previewAudioRef.current) previewAudioRef.current.pause();
        setIsPreviewing(false);
    };

    const handleExportVideo = async () => {
        setIsProcessing(true);
        setProcessingStatus('Mixing audio...');
        const totalDurationMs = imageSequence.reduce((acc, item, i) => acc + item.duration + (i < imageSequence.length - 1 ? transitionDuration : 0), 0);
        if (totalDurationMs <= 0) { alert("Cannot export a video with zero duration."); setIsProcessing(false); return; }
        const totalDurationSec = totalDurationMs / 1000;
        const offlineAudioContext = new OfflineAudioContext(2, Math.ceil(totalDurationSec * 44100), 44100);
        try {
            const transitionSoundBuffer = transitionSound
                ? await fetch(transitionSound).then(res => res.arrayBuffer()).then(ab => offlineAudioContext.decodeAudioData(ab))
                : null;

            const voiceoverBuffers = await Promise.all(imageSequence.map(item => item.audioUrl ? fetch(item.audioUrl).then(res => res.arrayBuffer()).then(ab => offlineAudioContext.decodeAudioData(ab)) : Promise.resolve(null)));

            let currentTime = 0;
            voiceoverBuffers.forEach((buffer, index) => {
                if (buffer) {
                    const source = offlineAudioContext.createBufferSource();
                    source.buffer = buffer;
                    source.connect(offlineAudioContext.destination);
                    source.start(currentTime / 1000);
                }

                const sceneEndTime = currentTime + imageSequence[index].duration;

                if (transitionSoundBuffer && index < imageSequence.length - 1) {
                    const soundSource = offlineAudioContext.createBufferSource();
                    soundSource.buffer = transitionSoundBuffer;

                    // --- MODIFIED ---: Apply volume using a GainNode
                    const gainNode = offlineAudioContext.createGain();
                    gainNode.gain.setValueAtTime(transitionSoundVolume, offlineAudioContext.currentTime);
                    soundSource.connect(gainNode);
                    gainNode.connect(offlineAudioContext.destination);

                    soundSource.start(sceneEndTime / 1000);
                }

                currentTime = sceneEndTime + (index < imageSequence.length - 1 ? transitionDuration : 0);
            });

            const renderedAudioBuffer = await offlineAudioContext.startRendering();
            setProcessingStatus('Rendering video frames...');
            const realTimeAudioContext = new AudioContext();
            const canvasStream = canvasRef.current.captureStream(30);
            const videoTrack = canvasStream.getVideoTracks()[0];
            const audioDestination = realTimeAudioContext.createMediaStreamDestination();
            const audioTrack = audioDestination.stream.getAudioTracks()[0];
            const combinedStream = new MediaStream([videoTrack, audioTrack]);
            const mediaRecorder = new MediaRecorder(combinedStream, { mimeType: 'video/webm;codecs=vp9,opus', videoBitsPerSecond: 10000000 });
            const recordedChunks = [];
            mediaRecorder.ondataavailable = e => { if (e.data.size > 0) recordedChunks.push(e.data); };
            mediaRecorder.onstop = () => {
                const blob = new Blob(recordedChunks, { type: 'video/webm' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                const sanitizedFilename = outputFilename.replace(/[^a-z0-9-_]/gi, '_').trim() || 'animation';
                a.download = `${sanitizedFilename}.webm`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                videoTrack.stop();
                audioTrack.stop();
                realTimeAudioContext.close();
                setIsProcessing(false);
                setProcessingStatus('');
            };
            const audioSource = realTimeAudioContext.createBufferSource();
            audioSource.buffer = renderedAudioBuffer;
            audioSource.connect(audioDestination);
            mediaRecorder.start();
            audioSource.start();
            let renderLoopId;
            const startTime = performance.now();
            const renderLoop = (now) => {
                const elapsedTime = now - startTime;
                drawFrameForTime(elapsedTime, totalDurationMs);
                if (elapsedTime <= totalDurationMs) renderLoopId = requestAnimationFrame(renderLoop);
                else { cancelAnimationFrame(renderLoopId); audioSource.stop(); mediaRecorder.stop(); }
            };
            renderLoopId = requestAnimationFrame(renderLoop);
        } catch (error) {
            console.error("Error during video export:", error);
            alert(`Export failed: ${error.message}`);
            setIsProcessing(false);
        }
    };

    const handleConvertToMp4 = async () => {
        if (!webmToConvert || !ffmpeg || isConverting) return;

        setIsConverting(true);
        setConversionProgress(0);

        try {
            const fileData = new Uint8Array(await webmToConvert.arrayBuffer());
            const inputFilename = 'input.webm';
            const outputFilename = 'output.mp4';

            await ffmpeg.writeFile(inputFilename, fileData);

            await ffmpeg.exec([
                '-fflags', '+genpts',
                '-i', inputFilename,
                '-r', '30',
                '-vf', 'scale=trunc(iw/2)*2:trunc(ih/2)*2',
                '-c:v', 'libx264',
                '-preset', 'ultrafast',
                '-c:a', 'aac',
                '-b:a', '192k',
                outputFilename
            ]);

            const data = await ffmpeg.readFile(outputFilename);
            const blob = new Blob([data.buffer], { type: 'video/mp4' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${webmToConvert.name.replace(/\.[^/.]+$/, "")}.mp4`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Error converting to MP4:", error);
            alert(`Conversion failed: ${error.message}`);
        } finally {
            setIsConverting(false);
            setConversionProgress(0);
        }
    };

    const redrawCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (selectedItem) {
            drawImage(selectedItem.image, { ...getTransform(selectedItem, 0), filter: selectedItem.filter });
            drawTextOverlay(selectedItem, 0);
            drawProgressBar(0);
        } else {
            ctx.fillStyle = '#111827';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#9ca3af';
            ctx.textAlign = 'center';
            ctx.font = '16px Arial';
            ctx.fillText('Upload images and select a scene to begin', canvas.width / 2, canvas.height / 2);
        }
    }, [selectedItem, drawImage, getTransform, drawTextOverlay, drawProgressBar,]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const container = canvas.parentElement;
        const resizeCanvas = () => {
            if (!container) return;
            const { clientWidth } = container;
            const aspect = 9 / 16;
            canvas.width = clientWidth > 0 ? clientWidth : 300;
            canvas.height = clientWidth > 0 ? clientWidth / aspect : 533;
            redrawCanvas();
        };
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
        return () => window.removeEventListener('resize', resizeCanvas);
    }, [redrawCanvas]);

    useEffect(() => {
        redrawCanvas();
    }, [selectedItem, redrawCanvas, showProgressBar, progressBarColor]);

    const handleSelectItem = (index) => {
        if (isPreviewing) handleStopPreview();
        setSelectedIndex(index);
    };

    const handleDeleteItem = (e, idToDelete) => {
        e.stopPropagation();
        const deletedIndex = imageSequence.findIndex(item => item.id === idToDelete);
        const newSequence = imageSequence.filter(item => item.id !== idToDelete);
        setImageSequence(newSequence);
        if (newSequence.length === 0) setSelectedIndex(-1);
        else if (selectedIndex === deletedIndex) setSelectedIndex(0);
        else if (selectedIndex > deletedIndex) setSelectedIndex(prev => prev - 1);
    }

    const handleDragStart = (e, id) => { setDraggedId(id); };
    const handleDragOver = (e) => { e.preventDefault(); };
    const handleDrop = (e, dropId) => {
        e.preventDefault();
        const draggedIndex = imageSequence.findIndex(p => p.id === draggedId);
        const dropIndex = imageSequence.findIndex(p => p.id === dropId);
        if (draggedIndex === -1 || dropIndex === -1) return;
        const newSequence = [...imageSequence];
        const [draggedItem] = newSequence.splice(draggedIndex, 1);
        newSequence.splice(dropIndex, 0, draggedItem);
        setImageSequence(newSequence);
        setSelectedIndex(newSequence.findIndex(p => p.id === draggedId));
        setDraggedId(null);
    };

    const playPreviewSound = () => {
        if (previewAudioRef.current) {
            previewAudioRef.current.volume = transitionSoundVolume; // --- MODIFIED ---: Apply volume
            previewAudioRef.current.currentTime = 0;
            previewAudioRef.current.play();
        }
    };

    useEffect(() => {
        if (transitionSound) {
            const audio = new Audio(transitionSound);
            audio.volume = transitionSoundVolume;
            previewAudioRef.current = audio;
        } else {
            previewAudioRef.current = null;
        }
    }, [transitionSound, transitionSoundVolume]); // --- MODIFIED ---: Update on volume change

    const selectedSoundValue = transitionSound ? transitionSound.split('/').pop() : "";

    return (
        <div className="image-animator-container">
            <div className="animator-controls-panel">
                <h1 className="text-2xl font-bold text-sky-300 text-center">Image Animator</h1>

                <div className="control-section">
                    <h3 className="control-heading">1. Upload Media</h3>
                    <div className="space-y-3">
                        <div><label className="animator-label">Images</label><input type="file" multiple accept="image/*" onChange={handleFileChange} className="animator-file-input accent-sky" /></div>
                        <div><label className="animator-label">Scripts (.json)</label><input type="file" accept=".json" onChange={handleScriptUpload} className="animator-file-input accent-purple" /></div>
                    </div>
                </div>

                <div className="control-section">
                    <h3 className="control-heading">2. Global Settings</h3>
                    <div className="mb-4"><label className="animator-label">OpenAI API Key</label><input type="password" placeholder="sk-..." value={openaiKey} onChange={(e) => setOpenaiKey(e.target.value)} className="animator-select bg-slate-800" /></div>
                    <label className="animator-label">Voice (applies to all slides)</label>
                    <select value={globalVoice} onChange={(e) => setGlobalVoice(e.target.value)} className="animator-select mb-4">{openAiVoices.map(v => <option key={v.name} value={v.name}>{v.label}</option>)}</select>
                    <div className="flex justify-between items-center gap-2 mb-2">
                        <label className="animator-label !mb-0">Voice Vibe</label>
                        <div className="flex gap-2 items-center">
                            <select className="text-xs border border-slate-500 rounded p-1 bg-slate-600 text-gray-200" value={globalVibe.preset} onChange={(e) => handleVibePresetChange(e.target.value)}>{Object.keys(vibePresets).map(name => <option key={name} value={name}>{name}</option>)}</select>
                            <button onClick={() => setGlobalVibe(prev => ({ ...prev, open: !prev.open }))} className="text-xs text-sky-400 hover:underline">{globalVibe.open ? "Hide" : "Show"} Details</button>
                        </div>
                    </div>
                    {globalVibe.open && (<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs p-2 border border-slate-600 rounded-md">
                        {(vibePresets["Custom"] ? Object.keys(vibePresets["Custom"]) : []).filter(k => k !== 'open' && k !== 'preset').map((key) => (<div key={key} className="flex flex-col">
                            <label className="block text-gray-400 font-medium mb-1 capitalize">{key.replace(/([A-Z])/g, ' $1')}</label>
                            <input type="text" value={globalVibe[key] || ""} onChange={(e) => handleVibeDetailChange(key, e.target.value)} className="animator-select bg-slate-800 text-xs p-1" />
                        </div>))}
                    </div>)}
                </div>

                <div className="control-section">
                    <h3 className="control-heading">3. Visuals & Transitions</h3>
                    <div className="mb-4">
                        <label className="animator-label">Transition Type</label>
                        <select value={transitionType} onChange={(e) => setTransitionType(e.target.value)} className="animator-select">
                            {transitionTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                    </div>
                    <div className="mb-4">
                        <label className="animator-label">Transition Duration: {transitionDuration}ms</label>
                        <input type="range" min="0" max="2000" step="100" value={transitionDuration} onChange={(e) => setTransitionDuration(parseInt(e.target.value, 10))} className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer accent-sky-500" />
                    </div>
                    <div className="space-y-2">
                        <label className="animator-label">Transition Sound</label>
                        <div className="flex items-center gap-2">
                            <select
                                value={selectedSoundValue}
                                onChange={(e) => setTransitionSound(e.target.value ? `/sounds/${e.target.value}` : null)}
                                className="animator-select"
                            >
                                <option value="">None</option>
                                {soundEffects.map(s => <option key={s.label} value={s.value}>{s.label}</option>)}
                            </select>
                            <button onClick={playPreviewSound} disabled={!transitionSound} className="p-2 bg-sky-600 hover:bg-sky-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed">
                                <PlayIcon className="h-5 w-5 text-white" />
                            </button>
                        </div>
                        {/* --- NEW ---: Volume slider */}
                        <div>
                            <label className="animator-label text-xs">Volume: {Math.round(transitionSoundVolume * 100)}%</label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                value={transitionSoundVolume}
                                onChange={(e) => setTransitionSoundVolume(parseFloat(e.target.value))}
                                className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer accent-sky-500"
                                disabled={!transitionSound}
                            />
                        </div>
                    </div>
                </div>

                <div className={`control-section transition-opacity duration-300 ${!selectedItem ? 'opacity-50 pointer-events-none' : ''}`}>
                    <h3 className="control-heading text-sky-400">4. Edit Selected Scene</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="animator-label">Voiceover Script</label>
                            <textarea rows="4" placeholder="Enter text for the voiceover..." value={selectedItem?.script || ''} onChange={(e) => handleControlChange('script', e.target.value)} className="animator-select bg-slate-800"></textarea>

                            <div className="flex items-center justify-start mt-2 mb-2">
                                <label htmlFor="duration-buffer-toggle" className="flex items-center gap-2 cursor-pointer text-sm text-slate-300 hover:text-white">
                                    <input
                                        type="checkbox"
                                        id="duration-buffer-toggle"
                                        checked={useDurationBuffer}
                                        onChange={(e) => setUseDurationBuffer(e.target.checked)}
                                        className="h-4 w-4 rounded bg-slate-600 border-slate-500 text-indigo-500 focus:ring-indigo-500"
                                    />
                                    Add 1s Buffer to Duration
                                </label>
                            </div>

                            <button onClick={handleGenerateAudio} disabled={selectedItem?.isGeneratingAudio || !selectedItem?.script.trim()} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">{selectedItem?.isGeneratingAudio ? 'Generating Audio...' : 'Generate Audio & Set Duration'}</button>
                            {selectedItem?.audioUrl && <audio src={selectedItem.audioUrl} controls className="mt-2 w-full h-8"></audio>}
                        </div>
                        <div className="p-3 bg-slate-900/50 rounded-lg">
                            <h4 className="font-semibold text-slate-300 mb-2">Animated Captions</h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div><label className="animator-label text-xs">Enabled</label><input type="checkbox" checked={selectedItem?.textOverlay?.enabled ?? false} onChange={(e) => handleTextOverlayChange('enabled', e.target.checked)} className="h-5 w-5 rounded bg-slate-600 border-slate-500 text-sky-500 focus:ring-sky-500" /></div>
                                <div><label className="animator-label text-xs">Text Color</label><input type="color" value={selectedItem?.textOverlay?.color || '#ffffff'} onChange={e => handleTextOverlayChange('color', e.target.value)} className="w-full h-8 p-0 border-0 rounded bg-slate-700 cursor-pointer" /></div>
                                <div><label className="animator-label text-xs">Font Size</label><input type="number" value={selectedItem?.textOverlay?.size || 16} onChange={e => handleTextOverlayChange('size', parseInt(e.target.value, 10))} className="animator-select text-sm p-1" /></div>
                            </div>
                            <div className="mt-4"><label className="animator-label text-xs">Vertical Position: {selectedItem?.textOverlay?.yPosition}%</label><input type="range" min="0" max="100" step="1" value={selectedItem?.textOverlay?.yPosition || 85} onChange={e => handleTextOverlayChange('yPosition', parseInt(e.target.value, 10))} className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer accent-sky-500" /></div>
                        </div>
                        <div className="p-3 bg-slate-900/50 rounded-lg">
                            <h4 className="font-semibold text-slate-300 mb-2">Visual Effects</h4>
                            <div><label className="animator-label">Animation</label><select value={selectedItem?.effect || 'none'} onChange={(e) => handleControlChange('effect', e.target.value)} className="animator-select"><option value="kenburns-up-right">Ken Burns Up-Right</option><option value="kenburns-down-right">Ken Burns Down-Right</option><option value="kenburns-up-left">Ken Burns Up-Left</option><option value="kenburns-down-left">Ken Burns Down-Left</option><option value="zoom-in">Zoom In</option><option value="zoom-out">Zoom Out</option><option value="none">None</option></select></div>
                            <div className="mt-4"><label className="animator-label">Scene Duration: {selectedItem?.duration || 0}ms</label><input type="range" min="500" max="20000" step="100" value={selectedItem?.duration || 0} onChange={(e) => handleControlChange('duration', parseInt(e.target.value, 10))} className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer accent-sky-500" /></div>
                            <div className="mt-4"><label className="animator-label">Zoom: {(selectedItem?.zoomAmount || 0).toFixed(2)}x</label><input type="range" min="0" max="1" step="0.05" value={selectedItem?.zoomAmount || 0} onChange={(e) => handleControlChange('zoomAmount', parseFloat(e.target.value))} className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer accent-sky-500" /></div>
                            <div className="mt-4"><label className="animator-label">Pan Amount: {((selectedItem?.panAmount || 0) * 100).toFixed(0)}%</label><input type="range" min="0" max="0.5" step="0.01" value={selectedItem?.panAmount || 0} onChange={(e) => handleControlChange('panAmount', parseFloat(e.target.value))} disabled={!selectedItem?.effect.startsWith('kenburns')} className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer accent-sky-500" /></div>
                            <div className="mt-4"><label className="animator-label">Color Filter</label><select value={selectedItem?.filter || 'none'} onChange={(e) => handleControlChange('filter', e.target.value)} className="animator-select">{visualFilters.map(f => <option key={f.name} value={f.name}>{f.label}</option>)}</select></div>
                        </div>
                    </div>
                </div>

                <div className="control-section mt-auto">
                    <h3 className="control-heading">5. Finalize & Export</h3>
                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <label htmlFor="showProgressBar" className="animator-label flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" id="showProgressBar" checked={showProgressBar} onChange={(e) => setShowProgressBar(e.target.checked)} className="h-4 w-4 rounded bg-slate-600 border-slate-500 text-purple-500 focus:ring-purple-500" />
                                Show Progress Bar
                            </label>
                            <input type="color" value={progressBarColor} onChange={(e) => setProgressBarColor(e.target.value)} className="w-10 h-8 p-1 border-0 rounded bg-slate-700 cursor-pointer" title="Progress Bar Color" />
                        </div>
                        <div><label className="animator-label">Output Filename (.webm)</label><input type="text" value={outputFilename} onChange={(e) => setOutputFilename(e.target.value)} placeholder="Enter video name" className="animator-select" /></div>
                        <div className="flex gap-2 mt-2">
                            {isPreviewing ? (<button onClick={handleStopPreview} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg">Stop Preview</button>) : (<button onClick={handlePreviewAnimation} disabled={isProcessing || imageSequence.length === 0} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50">Preview</button>)}
                            <button onClick={handleExportVideo} disabled={isProcessing || isPreviewing || imageSequence.length === 0} className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50">{isProcessing ? processingStatus : 'Download Video'}</button>
                        </div>
                    </div>
                    <div className="pt-4 mt-4 border-t border-slate-700">
                        <h3 className="text-md font-semibold text-teal-400 mb-2">Convert WEBM to MP4</h3>
                        {!isFfmpegLoaded ? (<div className="text-center text-slate-400">Loading converter...</div>) : (<div className="space-y-3">
                            <div><label className="animator-label">Upload .webm file</label><input type="file" accept=".webm" onChange={(e) => setWebmToConvert(e.target.files[0])} className="animator-file-input accent-teal" /></div>
                            {isConverting && (<div className="w-full bg-slate-600 rounded-full h-2.5"><div className="bg-teal-500 h-2.5 rounded-full" style={{ width: `${conversionProgress}%` }}></div></div>)}
                            <button onClick={handleConvertToMp4} disabled={!webmToConvert || isConverting} className="w-full bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">{isConverting ? `Converting... ${conversionProgress}%` : 'Convert to MP4'}</button>
                        </div>)}
                    </div>
                </div>
            </div>

            <div className="animator-main-content">
                <div className="animator-preview-area">
                    <div className="animator-canvas-container">
                        <canvas id="animator-canvas" ref={canvasRef}></canvas>
                    </div>
                </div>
                <div className="animator-timeline-container">
                    <h3 className="text-md font-semibold text-slate-300">Timeline (Drag to Re-order)</h3>
                    <div id="animator-timeline">
                        {imageSequence.map((item, index) => (
                            <div key={item.id} draggable onDragStart={(e) => handleDragStart(e, item.id)} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, item.id)} className={`timeline-item ${selectedIndex === index ? 'selected' : ''}`} onClick={() => handleSelectItem(index)}>
                                <img src={item.image.src} alt={`timeline-thumb-${item.id}`} />
                                {item.words && item.words.length > 0 && <div className="absolute top-1 left-1 text-xl" title="Has animated captions">🔠</div>}
                                {item.filter && item.filter !== 'none' && <FilmIcon className="h-5 w-5 absolute top-1.5 right-1.5 text-white/80" title={`Filter: ${item.filter}`} />}
                                <div className="timeline-item-overlay">{(item.duration / 1000).toFixed(1)}s</div>
                                <button className="timeline-item-delete" title="Delete item" onClick={(e) => handleDeleteItem(e, item.id)}>&times;</button>
                            </div>
                        ))}
                        {imageSequence.length === 0 && <div className="w-full h-40 flex items-center justify-center text-slate-500">Upload images to begin</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageAnimatorPage;
